import { useState, useEffect } from 'react'
import LoadingSpinner from '../common/LoadingSpinner'
import axios from 'axios'
import { toast } from 'react-toastify'
import { format } from 'date-fns'

const GoLivePanel = () => {
  const [loading, setLoading] = useState(true)
  const [readyGyms, setReadyGyms] = useState([])
  const [completedGyms, setCompletedGyms] = useState([])
  const [availableTeamMembers, setAvailableTeamMembers] = useState([])
  const [selectedGym, setSelectedGym] = useState(null)
  const [showGoLiveModal, setShowGoLiveModal] = useState(false)
  const [goLiveForm, setGoLiveForm] = useState({
    salesHeadId: '',
    opsHeadId: '',
    goLiveDate: '',
    finalComment: '',
    onboardRemarks: ''
  })
  const [processing, setProcessing] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(null)
  const [activeTab, setActiveTab] = useState('ready') // 'ready' or 'completed'

  useEffect(() => {
    fetchReadyGyms()
    fetchCompletedGyms()
    fetchAvailableTeamMembers()
  }, [])

  const fetchReadyGyms = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/api/ceo/gyms-ready-for-golive')
      setReadyGyms(response.data.readyGyms)
    } catch (error) {
      console.error('Error fetching ready gyms:', error)
      toast.error('Failed to load gyms ready for Go-Live')
    } finally {
      setLoading(false)
    }
  }

  const fetchCompletedGyms = async () => {
    try {
      const response = await axios.get('/api/gyms?stage=Gym Onboard')
      setCompletedGyms(response.data.gyms || [])
    } catch (error) {
      console.error('Error fetching completed gyms:', error)
    }
  }

  const fetchAvailableTeamMembers = async () => {
    try {
      const response = await axios.get('/api/ceo/handover-teams')
      setAvailableTeamMembers(response.data.availableTeamMembers)
    } catch (error) {
      console.error('Error fetching team members:', error)
      toast.error('Failed to load available team members')
    }
  }

  const handleGoLiveClick = (gym) => {
    setSelectedGym(gym)
    setGoLiveForm({
      salesHeadId: '',
      opsHeadId: '',
      goLiveDate: '',
      finalComment: '',
      onboardRemarks: ''
    })
    setShowGoLiveModal(true)
  }

  const handleGoLiveSubmit = async (e) => {
    e.preventDefault()

    if (!goLiveForm.salesHeadId || !goLiveForm.opsHeadId || !goLiveForm.goLiveDate) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      setProcessing(true)

      await axios.post(`/api/ceo/mark-golive/${selectedGym.id}`, goLiveForm)

      toast.success(`${selectedGym.name} marked as Gym Onboard!`)
      setShowGoLiveModal(false)
      setSelectedGym(null)

      // Refresh the lists
      fetchReadyGyms()
      fetchCompletedGyms()

    } catch (error) {
      console.error('Error marking Go-Live:', error)
      toast.error(error.response?.data?.error || 'Failed to mark gym as Gym Onboard')
    } finally {
      setProcessing(false)
    }
  }

  const handleDownloadPdf = async (gymId, gymName) => {
    try {
      setDownloadingPdf(gymId)

      const response = await axios.get(`/api/ceo/gym-report/${gymId}`, {
        responseType: 'blob'
      })

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `GoLive_Report_${gymName.replace(/[^a-zA-Z0-9]/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('PDF report downloaded successfully!')
    } catch (error) {
      console.error('Error downloading PDF:', error)
      toast.error('Failed to download PDF report')
    } finally {
      setDownloadingPdf(null)
    }
  }

  const closeModal = () => {
    setShowGoLiveModal(false)
    setSelectedGym(null)
    setGoLiveForm({
      salesHeadId: '',
      opsHeadId: '',
      goLiveDate: '',
      finalComment: '',
      onboardRemarks: ''
    })
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-green-900">Go-Live Center</h2>
            <p className="text-green-700 mt-1">
              Manage final gym approvals, handover to operational teams, and download reports
            </p>
          </div>
          <div className="flex space-x-6">
            <div className="text-center">
              <div className="text-green-700 text-sm">Ready for Go-Live</div>
              <div className="text-3xl font-bold text-green-800">{readyGyms.length}</div>
            </div>
            <div className="text-center">
              <div className="text-blue-700 text-sm">Go-Live Completed</div>
              <div className="text-3xl font-bold text-blue-800">{completedGyms.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-blue-800 font-medium">CEO Go-Live Authority</p>
            <p className="text-blue-700 text-sm mt-1">
              Only gyms with complete checklist approvals (HoA + COO + CEO) can be marked as Gym Onboard.
              You must assign Sales Head and Ops Head for operational handover. You can download PDF reports for completed gyms.
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('ready')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'ready'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Ready for Go-Live ({readyGyms.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'completed'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Go-Live Completed ({completedGyms.length})
          </button>
        </nav>
      </div>

      {/* Ready Gyms Tab */}
      {activeTab === 'ready' && (
        <>
          {readyGyms.length > 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Gyms Ready for Go-Live</h3>
                <p className="text-sm text-gray-600">All checklist items have been approved by you. Ready for operational handover.</p>
              </div>

              <div className="divide-y divide-gray-200">
                {readyGyms.map((gym) => (
                  <div key={gym.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h4 className="text-lg font-medium text-gray-900">{gym.name}</h4>
                          <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {gym.stage}
                          </span>
                        </div>

                        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Location:</span> {gym.location}
                          </div>
                          <div>
                            <span className="font-medium">Assigned Manager:</span> {gym.assignedUser
                              ? gym.assignedUser.name
                              : 'Unassigned'}
                          </div>
                          <div>
                            <span className="font-medium">Checklist Items:</span> {gym._count.checklistItems} (All Approved)
                          </div>
                        </div>

                        {/* Approval Summary */}
                        <div className="mt-3">
                          <div className="flex items-center space-x-4 text-xs">
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700">
                              Facility Team Complete
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700">
                              Head of Acquisition Approved
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700">
                              COO Approved
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700">
                              CEO Approved
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="ml-6">
                        <button
                          onClick={() => handleGoLiveClick(gym)}
                          className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Mark Gym Onboard
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
              <div className="text-center">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Gyms Ready for Go-Live</h3>
                <p className="text-gray-600">
                  Gyms will appear here once they reach the "Gym Onboard" stage.
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Completed Gyms Tab */}
      {activeTab === 'completed' && (
        <>
          {completedGyms.length > 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Go-Live Completed Gyms</h3>
                <p className="text-sm text-gray-600">Download PDF reports for completed gyms with full approval history and GPS-validated images.</p>
              </div>

              <div className="divide-y divide-gray-200">
                {completedGyms.map((gym) => (
                  <div key={gym.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h4 className="text-lg font-medium text-gray-900">{gym.name}</h4>
                          <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {gym.stage}
                          </span>
                        </div>

                        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Location:</span> {gym.location || 'N/A'}
                          </div>
                          <div>
                            <span className="font-medium">Assigned Manager:</span> {gym.assignedUser
                              ? gym.assignedUser.name
                              : 'Unassigned'}
                          </div>
                          <div>
                            <span className="font-medium">Go-Live Date:</span> {gym.launch_date
                              ? format(new Date(gym.launch_date), 'MMM dd, yyyy')
                              : 'N/A'}
                          </div>
                        </div>

                        {/* Display Onboard Remarks if available */}
                        {gym.onboardRemarks && (
                          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                            <div className="flex items-start">
                              <svg className="w-4 h-4 text-green-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                              </svg>
                              <div>
                                <span className="text-xs font-medium text-green-800">Onboard Remarks:</span>
                                <p className="text-sm text-green-700 mt-1">{gym.onboardRemarks}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="ml-6 flex space-x-3">
                        <button
                          onClick={() => handleDownloadPdf(gym.id, gym.name)}
                          disabled={downloadingPdf === gym.id}
                          className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {downloadingPdf === gym.id ? (
                            <>
                              <LoadingSpinner size="sm" className="mr-2" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Download PDF Report
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
              <div className="text-center">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Completed Go-Live Gyms</h3>
                <p className="text-gray-600">
                  Completed gyms will appear here after you mark them as Gym Onboard.
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Go-Live Modal */}
      {showGoLiveModal && selectedGym && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Mark Gym Onboard</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Assign operational handover teams for <strong>{selectedGym.name}</strong>
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleGoLiveSubmit} className="space-y-6">
                {/* Sales Head Assignment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign Sales Head <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={goLiveForm.salesHeadId}
                    onChange={(e) => setGoLiveForm(prev => ({ ...prev, salesHeadId: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  >
                    <option value="">Select Sales Head</option>
                    {availableTeamMembers.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.name} - {member.role.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Ops Head Assignment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign Ops Head <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={goLiveForm.opsHeadId}
                    onChange={(e) => setGoLiveForm(prev => ({ ...prev, opsHeadId: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  >
                    <option value="">Select Ops Head</option>
                    {availableTeamMembers.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.name} - {member.role.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Go-Live Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Go-Live Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={goLiveForm.goLiveDate}
                    onChange={(e) => setGoLiveForm(prev => ({ ...prev, goLiveDate: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>

                {/* Final Comment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Final CEO Comment
                  </label>
                  <textarea
                    value={goLiveForm.finalComment}
                    onChange={(e) => setGoLiveForm(prev => ({ ...prev, finalComment: e.target.value }))}
                    placeholder="Any final notes or instructions for the handover teams..."
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Onboard Remarks */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Onboard Remarks
                  </label>
                  <textarea
                    value={goLiveForm.onboardRemarks}
                    onChange={(e) => setGoLiveForm(prev => ({ ...prev, onboardRemarks: e.target.value }))}
                    placeholder="Add any remarks or notes about this gym onboarding..."
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    These remarks will be saved with the gym record for future reference.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processing ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Mark Gym Onboard
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GoLivePanel
